import React, {useCallback, useState, useEffect} from "react";
import {
  Form,
  FormFeedback,
  Row,
  Col,
  Container,
  Label,
  Input,
  Button,
} from "reactstrap";
import {useForm, Controller} from "react-hook-form";
import {useTranslation} from "react-i18next";
import {createClientUser, updateClientUser} from "../../api/user";
import { toast } from '../../utils/toast';
import {RUT_STATE, rutErrorKey, validateRut} from "../../utils/rut";
import {useCountry} from "../../context/CountryContext";
import {AuthService} from "../../services/authservice";

// Un RUT con contenido que no pasa el módulo 11 no se envía. Si el campo está vacío no
// bloqueamos aquí: eso lo sigue resolviendo el `required` de react-hook-form.
// Fuera de Chile este campo lleva el documento local (CUIT, NIT, cédula), así que el
// módulo 11 solo aplica con el país de operación en CL.
const rutBlocks = (value, esChile) =>
    esChile && String(value ?? "").trim() !== "" && !validateRut(value).ok;

export default function CreateClientBeartrack({
                                                edit,
                                                defaultData,
                                                closeModal,
                                                cerrarModal,
                                                fetch,
                                                setConfirmationModal,
                                                setPackagesCreated,
                                                user,
                                              }) {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);
  const [step, setStep] = useState(1);

  const titles = {
    form1: t("Datos de la empresa"),
    form2: t("Datos del administrador"),
    form3: t("Datos para retiros"),
  };

  const handleNextStep = () => {
    setStep((prevStep) => prevStep + 1);
  };

  const handlePrevStep = () => {
    setStep((prevStep) => prevStep - 1);
  };

  const methods = useForm({
    mode: "onSubmit",
    defaultValues: edit ? user : {},
  });

  useEffect(() => {
    if (edit && user) {
      methods.reset(user);
    }
  }, [edit, user, methods]);

  const ErrorMessage = ({msg}) => (
      <FormFeedback className="d-block">{msg}</FormFeedback>
  );

  const FormDivider = ({className}) => (
      <div className={`w-full border-t border-gray-300 my-2 ${className}`}/>
  );

  // RUT con módulo 11 (src/utils/rut.js): el DV equivocado se avisa al tiro; "incompleto"
  // recién al salir del campo, para no marcar en rojo a quien todavía está escribiendo.
  const { countryCode } = useCountry();
  const esChile = countryCode === "CL";
  const rutValue = methods.watch("rut");
  const rutCheck = validateRut(rutValue);
  const [rutTouched, setRutTouched] = useState(false);
  const rutError =
      esChile &&
      (rutCheck.state === RUT_STATE.BAD_DV ||
          (rutTouched && rutCheck.state === RUT_STATE.INCOMPLETE))
          ? rutErrorKey(rutCheck)
          : null;
  const rutBlocksSubmit = rutBlocks(rutValue, esChile);
  const RUT_ERROR_ID = edit ? "client-edit-rut-error" : "client-new-rut-error";

  const submitValues = useCallback(
      async (values) => {
        // Punto único de bloqueo: cubre también el submit implícito (Enter) que no pasa
        // por los botones deshabilitados.
        if (rutBlocks(values.rut, esChile)) {
          setRutTouched(true);
          return;
        }
        setLoading(true);
        const toastId = toast.loading(t("Guardando cambios..."));
        const accessToken = await AuthService.getValidAccessToken();

        if (accessToken) {
          values.token = accessToken;
        }

        try {
          if (edit) {
            await updateClientUser(values._id, values);
            toast.update(toastId, {
              appearance: "success",
              render: t("Usuario actualizado exitosamente"),
              type: "success",
              isLoading: false,
              autoClose: 2000,
              position: "bottom-center",
              autoDismiss: true,
            });
          } else {
            await createClientUser(values);
            toast.update(toastId, {
              appearance: "success",
              render: t("Usuario creado exitosamente"),
              type: "success",
              isLoading: false,
              autoClose: 2000,
              position: "bottom-center",
              autoDismiss: true,
            });
          }
          setLoading(false);
          closeModal(false);
          fetch(true);
        } catch (error) {
          setLoading(false);
          // Without a backend message show only the title (no dangling ": ");
          // longer autoClose because validation messages can be long.
          const msg = error?.message;
          toast.update(toastId, {
            appearance: "error",
            render: msg
              ? `${t("Error al guardar los cambios")}: ${msg}`
              : t("Error al guardar los cambios"),
            type: "error",
            isLoading: false,
            autoClose: 6000,
            position: "bottom-center",
            autoDismiss: true,
          });
        }
      },
      [closeModal, edit, fetch, user, esChile]
  );

  const getCurrentTitle = () => {
    switch (step) {
      case 1:
        return titles.form1;
      case 2:
        return titles.form2;
      case 3:
        return titles.form3;
      default:
        return titles.form1;
    }
  };

  const isForm1Complete =
      methods.watch("legalName") &&
      methods.watch("rut") &&
      !rutBlocksSubmit &&
      methods.watch("fantasyName") &&
      methods.watch("fiscalAddress");

  const isForm2Complete =
      methods.watch("name") &&
      methods.watch("email") &&
      methods.watch("phone");

  const isForm3Complete =
      methods.watch("senderAddress") &&
      methods.watch("preferedHour") &&
      methods.watch("namePayPerson") &&
      methods.watch("emailPayPerson");

  if (edit) {
    return (
      <div>
        <h1 className="text-black font-semibold text-xl mb-0 mt-0">
          {t("Datos del usuario")}
        </h1>
        <FormDivider/>
        <Container className="mt-0">
          <Form onSubmit={methods.handleSubmit(submitValues)}>
            <Row className="mt-0">
              <Col xs="12">
                <Label className="forms-input-title">{t("Nombre")}</Label>
                <Controller
                    name="name"
                    control={methods.control}
                    render={({field}) => (
                        <Input
                            {...field}
                            type="text"
                            className="p-2 px-3 forms-custom-input"
                            placeholder={t("Nombre")}
                        />
                    )}
                    rules={{required: t("Ingrese el nombre")}}
                />
                {methods.formState.errors?.name && (
                    <ErrorMessage
                        msg={methods.formState.errors?.name?.message}
                    />
                )}
              </Col>
              <Col xs="12">
                <Label className="forms-input-title">{t("Apellido")}</Label>
                <Controller
                    name="lastName"
                    control={methods.control}
                    render={({field}) => (
                        <Input
                            {...field}
                            type="text"
                            className="p-2 px-3 forms-custom-input"
                            placeholder={t("Apellido")}
                        />
                    )}
                    rules={{required: t("Ingrese el apellido")}}
                />
                {methods.formState.errors?.lastName && (
                    <ErrorMessage
                        msg={methods.formState.errors?.lastName?.message}
                    />
                )}
              </Col>
              <Col xs="12">
                <Label className="forms-input-title">{t("Correo electrónico")}</Label>
                <Controller
                    name="email"
                    control={methods.control}
                    render={({field}) => (
                        <Input
                            {...field}
                            type="email"
                            className="p-2 px-3 forms-custom-input"
                            placeholder={t("Correo")}
                        />
                    )}
                    rules={{required: t("Ingrese el correo")}}
                />
                {methods.formState.errors?.email && (
                    <ErrorMessage
                        msg={methods.formState.errors?.email?.message}
                    />
                )}
              </Col>
              <Col xs="12">
                <Label className="forms-input-title">{t("Número de teléfono")}</Label>
                <Controller
                    name="phone"
                    control={methods.control}
                    render={({field}) => (
                        <Input
                            {...field}
                            type="text"
                            className="p-2 px-3 forms-custom-input"
                            placeholder={t("Número de teléfono")}
                        />
                    )}
                    rules={{required: t("Ingrese el teléfono")}}
                />
                {methods.formState.errors?.phone && (
                    <ErrorMessage
                        msg={methods.formState.errors?.phone?.message}
                    />
                )}
              </Col>
              <Col xs="12">
                <Label className="forms-input-title">{t("RUT")}</Label>
                <Controller
                    name="rut"
                    control={methods.control}
                    render={({field}) => (
                        <Input
                            {...field}
                            type="text"
                            className="p-2 px-3 forms-custom-input"
                            placeholder={t("RUT")}
                            onBlur={(e) => {
                              field.onBlur(e);
                              setRutTouched(true);
                            }}
                            invalid={!!rutError}
                            aria-invalid={!!rutError}
                            aria-describedby={rutError ? RUT_ERROR_ID : undefined}
                        />
                    )}
                    rules={{required: t("Ingrese el RUT")}}
                />
                {rutError && (
                    <p id={RUT_ERROR_ID} className="text-xs text-status-error mt-1" role="alert">
                      {t(rutError, {dv: rutCheck.expectedDv})}
                    </p>
                )}
                {methods.formState.errors?.rut && (
                    <ErrorMessage
                        msg={methods.formState.errors?.rut?.message}
                    />
                )}
              </Col>
              <Col xs="12">
                <Label className="forms-input-title">{t("Estado")}</Label>
                <Controller
                    name="active"
                    control={methods.control}
                    render={({field}) => (
                        <Input
                            {...field}
                            type="select"
                            className="p-2 px-3 forms-custom-input"
                        >
                          <option value={true}>{t("Activo")}</option>
                          <option value={false}>{t("Inactivo")}</option>
                        </Input>
                    )}
                />
              </Col>
              <Col xs="12">
                <Label className="forms-input-title">{t("Empresa")}</Label>
                <Input
                    type="text"
                    className="p-2 px-3 forms-custom-input"
                    value={methods.watch("companyId.name") || ""}
                    disabled
                />
              </Col>
            </Row>
            <Row className="mt-4">
              <Col xs="6">
                <Button
                    color="orange"
                    className="w-100 bg-[#F8612C] text-white hover:bg-orange-500"
                    type="submit"
                    disabled={loading || rutBlocksSubmit}
                >
                  {t("Editar usuario")}
                </Button>
              </Col>
              <Col xs="6">
                <Button
                    color="white"
                    className="w-100 text-black border-black"
                    type="button"
                    onClick={() => {closeModal(false); cerrarModal(false)}}
                >
                  {t("Cancelar")}
                </Button>
              </Col>
            </Row>
          </Form>
        </Container>
      </div>
    );
  }

  return (
      <div>
        <h1 className="text-black font-semibold text-xl mb-0 mt-0">
          {getCurrentTitle()}
        </h1>
        <FormDivider/>
        <Container className="mt-0">
          <Form onSubmit={methods.handleSubmit(submitValues)}>
            {step === 1 && (
                <>
                  <Row className="mt-0">
                    <Col xs="12">
                      <Label className="forms-input-title">{t("Razón social")}</Label>
                      <Controller
                          name="legalName"
                          control={methods.control}
                          render={({field}) => (
                              <Input
                                  {...field}
                                  type="text"
                                  className="p-2 px-3 forms-custom-input"
                                  placeholder={t("GAMU SPA")}
                                  defaultValue=""
                              />
                          )}
                          rules={{required: t("Ingrese la Razón social")}}
                      />
                      {methods.formState.errors?.legalName && (
                          <ErrorMessage
                              msg={methods.formState.errors?.legalName?.message}
                          />
                      )}
                    </Col>
                    <Col xs="12">
                      <Label className="forms-input-title">{t("RUT")}</Label>
                      <Controller
                          name="rut"
                          control={methods.control}
                          render={({field}) => (
                              <Input
                                  {...field}
                                  type="text"
                                  className="p-2 px-3 forms-custom-input"
                                  placeholder={t("11.111.111-1")}
                                  defaultValue=""
                                  onBlur={(e) => {
                                    field.onBlur(e);
                                    setRutTouched(true);
                                  }}
                                  invalid={!!rutError}
                                  aria-invalid={!!rutError}
                                  aria-describedby={rutError ? RUT_ERROR_ID : undefined}
                              />
                          )}
                          rules={{required: t("Ingrese el RUT del vendedor")}}
                      />
                      {rutError && (
                          <p id={RUT_ERROR_ID} className="text-xs text-status-error mt-1" role="alert">
                            {t(rutError, {dv: rutCheck.expectedDv})}
                          </p>
                      )}
                      {methods.formState.errors?.rut && (
                          <ErrorMessage
                              msg={methods.formState.errors?.rut?.message}
                          />
                      )}
                    </Col>
                    <Col xs="12">
                      <Label className="forms-input-title">
                        {t("Nombre de fantasía")}
                      </Label>
                      <Controller
                          name="fantasyName"
                          control={methods.control}
                          render={({field}) => (
                              <Input
                                  {...field}
                                  type="text"
                                  className="p-2 px-3 forms-custom-input"
                                  placeholder={t("Entretenimientos GAMU")}
                                  defaultValue=""
                              />
                          )}
                          rules={{
                            required: t("Ingrese el Nombre de fantasía del vendedor"),
                          }}
                      />
                      {methods.formState.errors?.fantasyName && (
                          <ErrorMessage
                              msg={methods.formState.errors?.fantasyName?.message}
                          />
                      )}
                    </Col>
                    <Col xs="12">
                      <Label className="forms-input-title">{t("Dirección fiscal")}</Label>
                      <Controller
                          name="fiscalAddress"
                          control={methods.control}
                          render={({field}) => (
                              <Input
                                  {...field}
                                  type="text"
                                  className="p-2 px-3 forms-custom-input"
                                  placeholder={t("Providencia 111, Santiago")}
                                  defaultValue=""
                              />
                          )}
                          rules={{
                            required: t("Ingrese la Dirección fiscal del vendedor"),
                          }}
                      />
                      {methods.formState.errors?.fiscalAddress && (
                          <ErrorMessage
                              msg={methods.formState.errors?.fiscalAddress?.message}
                          />
                      )}
                    </Col>
                  </Row>
                  <Row className="mt-4">
                    <Col xs="6">
                      <Button
                          color="orange"
                          className={`w-100 bg-[#F8612C] text-white hover:bg-orange-500 ${
                              !isForm1Complete ? "disabled-button" : ""
                          }`}
                          type="button"
                          onClick={handleNextStep}
                          disabled={!isForm1Complete}
                      >
                        {t("Siguiente paso")}
                      </Button>
                    </Col>
                    <Col xs="6">
                      <Button
                          color="white"
                          className="w-100 text-black border-black"
                          type="button"
                          onClick={() => {closeModal(false); cerrarModal(false)}}
                      >
                        {t("Cancelar")}
                      </Button>
                    </Col>
                  </Row>
                </>
            )}

            {step === 2 && (
                <>
                  <Row className="mt-0">
                    <Col xs="12">
                      <Label className="forms-input-title">{t("Nombre")}</Label>
                      <Controller
                          name="name"
                          control={methods.control}
                          render={({field}) => (
                              <Input
                                  {...field}
                                  type="text"
                                  className="p-2 px-3 forms-custom-input"
                                  placeholder={t("Luis González")}
                                  defaultValue=""
                              />
                          )}
                          rules={{required: t("Ingrese el Nombre del vendedor")}}
                      />
                      {methods.formState.errors?.name && (
                          <ErrorMessage
                              msg={methods.formState.errors?.name?.message}
                          />
                      )}
                    </Col>
                    <Col xs="12">
                      <Label className="forms-input-title">
                        {t("Correo electrónico")}
                      </Label>
                      <Controller
                          name="email"
                          control={methods.control}
                          render={({field}) => (
                              <Input
                                  {...field}
                                  type="email"
                                  className="p-2 px-3 forms-custom-input"
                                  placeholder={t("luis@beatrackapp.com")}
                                  defaultValue=""
                              />
                          )}
                          rules={{
                            required: t("Ingrese el Correo electrónico del vendedor"),
                          }}
                      />
                      {methods.formState.errors?.email && (
                          <ErrorMessage
                              msg={methods.formState.errors?.email?.message}
                          />
                      )}
                    </Col>
                    <Col xs="12">
                      <Label className="forms-input-title">{t("Teléfono")}</Label>
                      <Controller
                          name="phone"
                          control={methods.control}
                          render={({field}) => (
                              <Input
                                  {...field}
                                  type="text"
                                  className="p-2 px-3 forms-custom-input"
                                  placeholder={t("987654321")}
                                  defaultValue=""
                              />
                          )}
                          rules={{required: t("Ingrese el Teléfono del vendedor")}}
                      />
                      {methods.formState.errors?.phone && (
                          <ErrorMessage
                              msg={methods.formState.errors?.phone?.message}
                          />
                      )}
                    </Col>
                  </Row>
                  <Row className="mt-4">
                    <Col xs="6">
                      <Button
                          color="orange"
                          className={`w-100 bg-[#F8612C] text-white hover:bg-orange-500 ${
                              !isForm2Complete ? "disabled-button" : ""
                          }`}
                          type="button"
                          onClick={handleNextStep}
                          disabled={!isForm2Complete}
                      >
                        {t("Siguiente paso")}
                      </Button>
                    </Col>
                    <Col xs="6">
                      <Button
                          color="white"
                          className="w-100 text-black border-black"
                          type="button"
                          onClick={handlePrevStep}
                      >
                        {t("Volver atrás")}
                      </Button>
                    </Col>
                  </Row>
                </>
            )}

            {step === 3 && (
                <>
                  <Row className="mt-0">
                    <Col xs="12">
                      <Label className="forms-input-title">
                        {t("Dirección de retiro")}
                      </Label>
                      <Controller
                          name="senderAddress"
                          control={methods.control}
                          render={({field}) => (
                              <Input
                                  {...field}
                                  type="text"
                                  className="p-2 px-3 forms-custom-input"
                                  placeholder={t("General Gana 123, Santiago")}
                                  defaultValue=""
                              />
                          )}
                          rules={{
                            required: t("Ingrese la Dirección de retiro del vendedor"),
                          }}
                      />
                      {methods.formState.errors?.senderAddress && (
                          <ErrorMessage
                              msg={methods.formState.errors?.senderAddress?.message}
                          />
                      )}
                    </Col>
                    <Col xs="12">
                      <Label className="forms-input-title">{t("Horario de retiro")}</Label>
                      <Controller
                          name="preferedHour"
                          control={methods.control}
                          render={({field}) => (
                              <Input
                                  {...field}
                                  type="text"
                                  className="p-2 px-3 forms-custom-input"
                                  placeholder={t("13:00")}
                                  defaultValue=""
                              />
                          )}
                          rules={{
                            required: t("Ingrese el horario de retiro del vendedor"),
                          }}
                      />
                      {methods.formState.errors?.preferedHour && (
                          <ErrorMessage
                              msg={methods.formState.errors?.preferedHour?.message}
                          />
                      )}
                    </Col>
                    <h1 className="text-black font-semibold text-xl mb-0 mt-4">
                      {t("Datos para cobros")}
                    </h1>
                    <FormDivider></FormDivider>
                    <Col xs="12">
                      <Label className="forms-input-title">
                        {t("Responsable cobros")}
                      </Label>
                      <Controller
                          name="namePayPerson"
                          control={methods.control}
                          render={({field}) => (
                              <Input
                                  {...field}
                                  type="text"
                                  className="p-2 px-3 forms-custom-input"
                                  placeholder={t("Gerardo Morales")}
                                  defaultValue=""
                              />
                          )}
                          rules={{required: t("Ingrese el Responsable de cobros")}}
                      />
                      {methods.formState.errors?.namePayPerson && (
                          <ErrorMessage
                              msg={methods.formState.errors?.namePayPerson?.message}
                          />
                      )}
                    </Col>
                    <Col xs="12">
                      <Label className="forms-input-title">
                        {t("Correo responsable cobros")}
                      </Label>
                      <Controller
                          name="emailPayPerson"
                          control={methods.control}
                          render={({field}) => (
                              <Input
                                  {...field}
                                  type="email"
                                  className="p-2 px-3 forms-custom-input"
                                  placeholder={t("gmorales@grupogamu.cl")}
                                  defaultValue=""
                              />
                          )}
                          rules={{
                            required: t("Ingrese el correo del responsable de cobros"),
                          }}
                      />
                      {methods.formState.errors?.emailPayPerson && (
                          <ErrorMessage
                              msg={methods.formState.errors?.emailPayPerson?.message}
                          />
                      )}
                    </Col>
                  </Row>
                  <Row className="mt-4">
                    <Col xs="6">
                      <Button
                          color="orange"
                          className={`w-100 bg-[#F8612C] text-white hover:bg-orange-500 ${
                              !isForm3Complete ? "disabled-button" : ""
                          }`}
                          type="submit"
                          disabled={!isForm3Complete}
                      >
                        {t("Crear usuario")}
                      </Button>
                    </Col>
                    <Col xs="6">
                      <Button
                          color="white"
                          className="w-100 text-black border-black"
                          type="button"
                          onClick={() => {closeModal(false); cerrarModal(false)}}
                      >
                        {t("Cancelar")}
                      </Button>
                    </Col>
                  </Row>
                </>
            )}
          </Form>
        </Container>
      </div>
  );
}